// Read-only rendering of plan/template items, matching PlanItemsEditor's SS/CC visualization.
// Used by PreviewPlanModal and LoadHistoryPage detail view.

const GROUP_COLORS_SS = [
  { border: "#3B82F6", bg: "rgba(59,130,246,0.10)", label: "A" },  // Superset base = BLU
  { border: "#8B5CF6", bg: "rgba(139,92,246,0.10)", label: "B" },
  { border: "#06B6D4", bg: "rgba(6,182,212,0.10)", label: "C" },
  { border: "#0EA5E9", bg: "rgba(14,165,233,0.10)", label: "D" },
];
const GROUP_COLORS_CC = [
  { border: "#F97316", bg: "rgba(249,115,22,0.12)", label: "A" },  // Circuito base = ARANCIONE
  { border: "#EC4899", bg: "rgba(236,72,153,0.10)", label: "B" },
  { border: "#EAB308", bg: "rgba(234,179,8,0.10)", label: "C" },
  { border: "#EF4444", bg: "rgba(239,68,68,0.10)", label: "D" },
];

// Deterministic color for an item based on its group_id + type, computed globally over the plan's items.
export function itemGroupColor(item, allItems) {
  if (!item.group_id) return null;
  const palette = item.group_type === "circuit" ? GROUP_COLORS_CC : GROUP_COLORS_SS;
  const idx = {};
  (allItems || []).forEach(it => {
    if (it.group_id && it.group_type === item.group_type && !(it.group_id in idx)) {
      idx[it.group_id] = Object.keys(idx).length;
    }
  });
  const i = (idx[item.group_id] ?? 0) % palette.length;
  return palette[i];
}

/**
 * PlanItemsView
 * @param items array of {id, exercise_id, sets, reps, rest_seconds, notes, group_id, group_type}
 * @param exercises exercise library
 * @param renderExtra optional function(item, index) rendering extra content inside each item card
 * @param testidPrefix optional testid prefix (default: "pv-item")
 */
export default function PlanItemsView({ items = [], exercises = [], renderExtra, testidPrefix = "pv-item" }) {
  const exMap = Object.fromEntries((exercises || []).map(e => [e.id, e]));
  if (!items || items.length === 0) {
    return <div className="border border-white/5 p-6 text-center text-white/40 text-sm">Nessun esercizio.</div>;
  }
  return (
    <div className="space-y-2">
      {items.map((it, i) => {
        const ex = exMap[it.exercise_id];
        const color = itemGroupColor(it, items);
        const groupLabel = it.group_type === "superset" ? "Super-set" : it.group_type === "circuit" ? "Circuito" : null;
        const boxStyle = color ? { borderColor: color.border, background: color.bg, borderWidth: 2 } : {};
        return (
          <div key={it.id || i}
            data-testid={`${testidPrefix}-${i}`}
            style={boxStyle}
            className={`border p-3 min-w-0 ${color ? "" : "border-white/10"}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-7 h-7 flex items-center justify-center border border-white/10 font-heading font-bold text-xs shrink-0">{i + 1}</div>
              <div className="font-heading font-bold text-sm uppercase tracking-tight">{ex?.name || "—"}</div>
              {groupLabel && color && (
                <span
                  data-testid={`${testidPrefix}-${i}-group`}
                  style={{ background: color.border, color: "#fff" }}
                  className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">
                  {groupLabel} {color.label}
                </span>
              )}
            </div>
            {ex?.muscle_group && (
              <div className="text-[10px] uppercase tracking-widest text-[#A855F7] mt-1">{ex.muscle_group}</div>
            )}
            <div className="text-[11px] text-white/70 mt-2">
              <span className="text-white/40">Serie:</span> {it.sets ?? "—"}
              {" · "}<span className="text-white/40">Rip.:</span> {it.reps ?? "—"}
              {" · "}<span className="text-white/40">Rec.:</span> {it.rest_seconds ?? "—"}s
            </div>
            {it.notes && <div className="text-[11px] text-white/60 italic mt-1">Note: {it.notes}</div>}
            {typeof renderExtra === "function" && renderExtra(it, i)}
          </div>
        );
      })}
    </div>
  );
}
